<script setup>
  import { computed, ref } from 'vue';

  import CredereButton from '@/components/Core/Button.vue';

  const props = defineProps({
    color: {
      type: String,
      required: false,
      default: 'danger',
      validator(value) {
        return ['danger', 'success', 'information'].includes(value);
      },
    },
    // Drops the block's bottom margin so the alert sits flush inside a tight
    // container (e.g. a thread bubble) that owns its own spacing.
    marginless: {
      type: Boolean,
      required: false,
      default: false,
    },
    /*
     * O AVISO QUE É A TELA, e não uma tarja dentro dela: mais respiro e a barra de cor mais
     * grossa. É a `variant--big` da pwa (`_CORDEL/components/Context/Alert.vue`), onde ela
     * serve o aviso que ocupa a página sozinho.
     *
     * O respiro EXTRA SÓ EXISTE ENQUANTO CABE. Numa caixa estreita, 48px de cada lado comem
     * a frase - a pwa reduzia isso no mobile e aqui a redução é por CONTAINER, no mesmo
     * degrau de 480px que o resto deste arquivo já usa (a caixa é o que aperta a frase, não
     * o aparelho). A barra de cor fica: ela custa largura fixa e é o que identifica o aviso.
     */
    big: {
      type: Boolean,
      required: false,
      default: false,
    },
    /*
     * O AVISO CLARO, que é o da pwa (`_CORDEL/components/Context/Alert.vue`, `variant--light`):
     * fundo `highlight-light` e texto escuro, no lugar da superfície `constant-dark`.
     *
     * É para o aviso que NÃO interrompe - o card de totais das aprovadas é o caso: ele resume
     * a coleção que a pessoa veio ver, e em cima da superfície escura ele lia como alerta de
     * problema numa tela onde não há problema nenhum.
     *
     * A barra de cor acompanha a cor do aviso: no `success` ela vai a `highlight-dark`, que é
     * o verde que se enxerga sobre o claro (o `feedback-success` do escuro sumiria nele).
     *
     * A ACAO NAO TROCA DE COR: ela e `highlight` nas duas superficies. Houve aqui um
     * `primary` para o claro, pelo argumento de que verde sobre verde-claro perde
     * contraste; foi avaliado e recusado, e fica registrado aqui em vez de ser refeito.
     */
    light: {
      type: Boolean,
      required: false,
      default: false,
    },
    // WHERE THE ALERT SITS in what surrounds it, which is what decides on which
    // side it needs air:
    //
    //   * `top`    - margin below (the default: an alert that opens a block)
    //   * `middle` - margin above and below
    //   * `bottom` - margin above, nothing below (an alert that closes a block)
    //
    // `marginless` is a different axis and WINS over this one: it means "no
    // margin at all", the more specific instruction ("the container owns the
    // spacing"), and it wins by construction - it redeclares the margin
    // shorthand these positions only write the edges of.
    //
    // Whatever the position, an alert IMMEDIATELY FOLLOWED BY ANOTHER shrinks
    // the space below it on its own (see the stylesheet): stacked alerts are one
    // block, and no caller has to say so.
    position: {
      type: String,
      required: false,
      default: 'top',
      validator(value) {
        return ['top', 'middle', 'bottom'].includes(value);
      },
    },
    /*
     * THE ACTION, and the alert renders the button itself rather than taking one
     * from the caller. `button` is its label; null means this alert has no
     * action.
     *
     * The COLOUR is why this is a prop and not a slot. The alert's surface is
     * `constant-dark`, and the Button's default `primary` (`neutral-darker`)
     * lands at 1.26:1 against it - a control with no perceived edge, far under
     * the 3:1 WCAG 1.4.11 asks of a component boundary, and invisible to axe
     * because the label itself is white and passes. `highlight` reads at 7.82:1
     * and is already the house's affirmative action. Behind a slot that
     * knowledge lived in whoever remembered it, and the one place that forgot
     * was this component's own story. The surface is the alert's, so the
     * constraint it imposes is the alert's to hold.
     *
     * The SIZE is the alert's by the same argument and the same hand, and it
     * FOLLOWS THE SIZE OF THE ALERT (see `buttonSize`): `small` in the strip,
     * `medium` in the `big` - designer decisions, taken on 2026-08-24 and
     * 2026-09-02. Neither reaches the 44px of WCAG 2.5.5 without a change at
     * `Core/Button` - `medium` measures 43.6px wide and shrinks to ~40.8px in a
     * narrow container, `small` holds 32.8px everywhere - so the decision was
     * between two that miss, and it is recorded with both measurements in
     * DESIGN-PRINCIPLES §6 rather than re-argued here. The `×` is a separate
     * control and still clears the floor at 47.2px.
     *
     * THE `small` HAS LEFT ONCE AND COME BACK, so it is worth saying why it is
     * not boilerplate: it was dropped in the same commit that took the action's
     * colour back to `highlight` (16eefb5), as if it were part of that question,
     * and the default `medium` took over the strip as well - 43.6px of button
     * inside 14px of alert padding, a 71.6px row where the notice with no action
     * measures 49px, and 20px of the sentence's width eaten by the wider
     * button. Nothing went red, because nothing measured it - which is why the
     * three places that document `small` went on saying it while the code had
     * stopped. The `Com ação` and `Grande` stories now measure the step each
     * surface takes, so a size leaving again is a red story and not a silent
     * change of a designer decision.
     *
     * What the caller still owns is the whole point of the action: the word and
     * the handler. What it no longer owns is a choice it could only get wrong.
     */
    button: {
      type: String,
      required: false,
      default: null,
    },

    /*
     * O DESTINO DA AÇÃO, quando ela é um LUGAR e não um efeito. Havendo `href`, o controle é
     * uma âncora de verdade: menu de contexto, cmd+clique e botão do meio passam a existir, e
     * são do navegador - nenhum handler os oferece.
     *
     * É o caso do card de totais da home, cuja ação leva à listagem inteira. Sem `href` o
     * controle segue sendo botão e quem decide o que acontece é o `@click`.
     */
    buttonHref: {
      type: String,
      required: false,
      default: null,
    },

    // Feeds the Button's own `loading`, so an action that goes to the network
    // reports back on the control the person just pressed.
    buttonLoading: {
      type: Boolean,
      required: false,
      default: false,
    },

    /*
     * Adds the close control. Opt-in, so each screen declares the intent: on a
     * screen the person went to precisely to fix the thing, the notice must
     * stay put; where the same notice would interrupt work it can be closed.
     *
     * The scope of closing is this instance's lifetime and nothing more -
     * remount and the alert is back. That is the whole feature, not a missing
     * half: "close forever" hides a problem that is still there and the person
     * stops being reminded to solve it, while closing for now only clears the
     * message out of the way. A screen that swaps the message in place instead
     * of remounting should therefore `:key` the alert on the message, or a
     * dismissed alert will swallow the next one.
     */
    dismissible: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const emit = defineEmits(['click', 'close']);

  /*
   * O DEGRAU DA AÇÃO ACOMPANHA O DO AVISO: `small` na tarja, `medium` no `big`.
   *
   * O `small` existe porque a tarja é uma linha só - um botão de 43.6px dentro de 14px de
   * respiro faz uma linha de 71.6px onde o aviso sem ação mede 49px. No `big` não há linha
   * para estourar: o respiro já é de 28px por 48px, o símbolo já cresceu, e é o aviso que
   * ocupa a tela - o botão compacto ali fica pequeno para o que a ação vale. É o que o
   * componente do Cordel desenha (rótulo de 14px, o mesmo do `medium`).
   *
   * Uma computed e não uma constante porque o `big` pode mudar depois do mount (um controle
   * do Storybook, uma tela que troca a variante), e um valor lido uma vez no setup
   * congelaria o degrau no primeiro props - o mesmo motivo do `classes` abaixo.
   */
  const buttonSize = computed(() => (props.big ? 'medium' : 'small'));

  /*
   * The dismiss is the only place this product animates *layout* and not just
   * paint, and the two tracks are the whole point. Fading the text out over
   * 120ms empties the box early; closing the box over 260ms then lets whatever
   * sits below it rise instead of snapping, which is the part a plain fade never
   * fixed - it left the hole open for the full duration and then collapsed it in
   * a single frame.
   *
   * The curve is the standard symmetric ease, and it is the second one tried: a
   * hard decelerate (`0.2, 0, 0, 1`) read as *stuck*, not smooth, because it
   * spent well over half the duration crawling the last third of the height
   * shut while the contents were already at zero opacity - an empty gap that
   * lingers. Easing evenly at 220ms keeps the whole close in one gesture.
   */
  const FADE_DURATION = 120;
  const COLLAPSE_DURATION = 220;
  const COLLAPSE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const visible = ref(true);

  // `click` is the action and `close` is the dismiss. The name follows
  // `Core/CalloutCard`, the other component that renders a button on its caller's
  // behalf, rather than inventing a second word for the same event.
  function click() {
    emit('click');
  }

  // A computed and not a plain const: `marginless` and `color` can both change
  // after mount (a Storybook control, a screen that re-colours a live alert),
  // and a value read once at setup would freeze the class list on its first
  // props.
  const classes = computed(() => ({
    'credere-alert': true,
    [`color--${props.color}`]: true,
    [`position--${props.position}`]: true,
    'variant--marginless': props.marginless,
    'variant--big': props.big,
    'variant--light': props.light,
  }));

  function close() {
    visible.value = false;

    emit('close');
  }

  /*
   * Driven from JS rather than from `*-leave-active` classes because the height
   * has to be measured before it can be animated: `height: auto` has nothing to
   * interpolate from. The CSS-only ways out are both worse here - `max-height`
   * needs a magic number that clips a three-line alert, and `interpolate-size`
   * is Chromium-only, which a widget that lands on whatever browser the dealer
   * runs cannot rely on.
   */
  function leave(element, done) {
    // Someone who asked the system for less movement gets the result with none
    // of it, not a shorter version of it.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();

      return;
    }

    const { height } = element.getBoundingClientRect();
    const { marginBottom, paddingTop, paddingBottom } = getComputedStyle(element);

    /*
     * The vertical padding gets its own tracks because `height` alone cannot
     * close the box: under `box-sizing: border-box` a used height below
     * padding + border is clamped to padding + border, so the collapse stalls at
     * exactly `--padding` top+bottom and then loses that last strip in a single
     * frame when the element unmounts - measured as a 32px jump of everything
     * below, which is the precise jolt this animation exists to remove.
     *
     * These are read off the computed style rather than written as lengths, so
     * the spacing still comes from `--padding` and its token; the animation only
     * takes what is already there down to zero.
     */
    element.style.overflow = 'hidden';

    element.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: FADE_DURATION, easing: 'linear', fill: 'forwards' },
    );

    const collapse = element.animate(
      [
        {
          height: `${height}px`, marginBottom, paddingTop, paddingBottom,
        },
        {
          height: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px',
        },
      ],
      { duration: COLLAPSE_DURATION, easing: COLLAPSE_EASING, fill: 'forwards' },
    );

    // Both settle the same way: `finished` rejects when an animation is
    // cancelled, which happens if the alert is torn down mid-flight, and Vue
    // still needs its callback or the element never leaves the DOM.
    collapse.finished.then(done, done);
  }
</script>

<template>
  <!--
    `:css="false"` tells Vue not to look for transition classes at all, so the
    only thing that ends the leave is `done()` from the hook below. Enter has no
    hook on purpose: the alert appearing is the product reporting a failure and
    should be immediate, and only the leaving is the person's own doing.
  -->
  <transition
    :css="false"
    @leave="leave"
  >
    <section
      v-if="visible"
      :class="classes"
    >
      <!-- O SÍMBOLO E A MENSAGEM SÃO UMA LINHA DENTRO DA LINHA, e é a única forma de o
           símbolo ficar na PRIMEIRA LINHA do texto sem arrastar o texto junto: a linha de
           fora precisa centrar (o bloco de texto contra a ação, ver o `variant--big`), e a
           de dentro precisa começar no topo (o símbolo contra a primeira linha). Com um
           `align-items` só, escolher o topo pendura a frase no alto da faixa que o botão
           mede - 43.6px contra os 21px dela -, e escolher o centro desce o símbolo para o
           meio de um bloco de duas linhas. São dois eixos, então são dois elementos, que é
           como o componente do Cordel também se estrutura (`icon + header` dentro de
           `inline`). -->
      <div class="credere-alert-content">
        <span class="credere-alert-icon">!</span>

        <div class="credere-alert-paragraph">
          <slot />
        </div>
      </div>

      <!-- Beside the sentence in the alert's own row and never under it - the
           row holds the text block, this and the `×`, in that order: every
           alert that offers an action puts it in the same place, and
           renders it in the one colour that reads on this surface. The caller
           brings the word and the handler; the rest is the alert's, because the
           rest is a property of the surface and not of the intent.

           `type="button"` is load-bearing for the same reason it is on the close
           control below: `Form/SchemaForm.vue` renders this alert as the first
           child of its `<form>`, and `Core/Button` leaves `type` unset, so a
           bare action there would submit the form it is sitting on top of. -->
      <div
        v-if="button"
        class="credere-alert-action"
      >
        <credere-button
          class="credere-alert-action-button"
          color="highlight"
          :href="buttonHref"
          :size="buttonSize"
          type="button"
          :loading="buttonLoading"
          @click="click"
        >
          {{ button }}
        </credere-button>
      </div>

      <!--
        `type="button"` is load-bearing, not boilerplate: `Form/SchemaForm.vue`
        renders this alert as the first child of its `<form>`, and a bare
        `<button>` there defaults to `submit` - closing the error would submit
        the form that produced it.
      -->
      <button
        v-if="dismissible"
        class="credere-alert-close"
        type="button"
        aria-label="Fechar aviso"
        @click="close"
      >
        <font-awesome-icon icon="times" />
      </button>
    </section>
  </transition>
</template>

<style lang="scss">
  @import '@css/mixins/mobile.scss';

  .credere-alert {
    --background-color: var(--cordel-color-constant-dark);
    --border-color: var(--cordel-color-feedback-danger);
    --border-style: solid;
    --border-width: 0 0 0 var(--cordel-spacing-xxxs);
    --border-radius: var(--cordel-border-radius-small);
    --color: var(--cordel-color-constant-light);
    --font-size: var(--cordel-font-size-m);
    // O QUE ESTE `--gap` SEPARA é o bloco de texto, a ação e o `×`: o espaço entre o
    // símbolo e a frase é do `.credere-alert-content`, que os agrupa.
    --gap: var(--cordel-spacing-xxxs);
    --line-height: var(--cordel-line-height-l);
    // THE TWO EDGES, SEPARATELY, so a rule can move one without erasing the
    // other: `--margin` is a shorthand, and any block that redeclares it to
    // change the bottom also silently zeroes the top a `position` had put there.
    // Namespaced because the reset (`* { @include tokens-reset }`) owns the bare
    // token names and does NOT declare these - a bare `--margin-top` here would
    // inherit into every descendant of the alert.
    --alert-margin-top: 0;
    --alert-margin-bottom: var(--cordel-spacing-blaster);
    --margin: var(--alert-margin-top) 0 var(--alert-margin-bottom) 0;
    --padding: var(--cordel-spacing-xxs);

    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    @include mobile-font-size;

    /*
     * AT MOBILE THE ROW BREAKS IN TWO: the sentence keeps the × beside it, and
     * the action drops to a full-width line under both. Wide, the sentence gives
     * up its width to a button that never shrinks (`flex-shrink: 0` below), and
     * that is right until the sentence has no width left to give - measured with
     * the long copy at the mobile type scale, the text column runs 502px at
     * 768px of container, 239px at 480px, then 134px at 375px and 79px over six
     * lines at 320px.
     *
     * So the break is at 480px and not at the 768px `mobile-font-size` uses:
     * everything from 480px up still reads on one or two lines, and a
     * full-width button across a 700px container is a CTA the size of the notice
     * it belongs to. `Core/CalloutCard` breaks at 768px because its card is
     * already a stacked column at that width; this is a single row, so it earns
     * its own number - the same split DESIGN-PRINCIPLES §7 records for
     * `Showroom/TopBanner`, layout breakpoint kept separate from the typographic
     * one.
     *
     * Gated on the container ALONE, with none of `mobile-font-size`'s second
     * condition on the host viewport. Type follows the device, but layout follows
     * the box it has to fit in: a 320px column on a desktop page squeezes the
     * sentence exactly as hard as a phone does, and that pair is a real case the
     * `Coluna de desktop` preset exists to show.
     */
    @container (max-width: 480px) {
      // Row gap first, column gap second: the 26px is the space the two lines
      // need between them, while the icon and the sentence keep the tighter
      // spacing they have wide.
      --gap: var(--cordel-spacing-xxl) var(--cordel-spacing-xxxs);

      flex-wrap: wrap;
    }

    // `position--top` has no block on purpose: it IS the pair declared above, so
    // the default keeps behaving exactly as it did before the prop existed (the
    // same idiom `ButtonGroup` uses for `spacing--none`).
    &.position--middle {
      --alert-margin-top: var(--cordel-spacing-blaster);
    }

    &.position--bottom {
      --alert-margin-top: var(--cordel-spacing-blaster);
      --alert-margin-bottom: 0;
    }

    // TWO ALERTS IN A ROW ARE ONE BLOCK, and the full margin between them reads
    // as two - the gate notices in Conexões com a Meta are the case that showed
    // it. It touches the BOTTOM EDGE ONLY, which is the whole reason the edges
    // are separate variables: written as a `--margin` this would also wipe the
    // top margin of a `position--middle` alert that happens to be followed by
    // another (Automations/Notifications stack exactly that pair).
    //
    // AFTER the positions on purpose, and they weigh the same (0,2,0), so source
    // order is what decides: it also overrides `position--bottom`'s zero, which
    // is right - "closes the block" stops being true the moment another alert
    // follows.
    //
    // It does NOT reach an alert whose caller declares its own `--margin`: that
    // declaration replaces the shorthand, so the edge variables stop being read
    // at all. Deliberate - a caller that spelled out its spacing means it - and
    // no screen does today, the Inbox thread having handed its three alerts'
    // spacing back to this component (wip#1398).
    &:has(+ .credere-alert) {
      --alert-margin-bottom: var(--cordel-spacing-s);
    }

    // Still last, but no longer BECAUSE of source order: "no margin at all"
    // redeclares the shorthand, so it wins over the edge variables by
    // construction - none of the blocks above can undo it anymore.
    &.variant--marginless {
      --margin: 0;
    }

    /*
     * O AVISO QUE OCUPA A PÁGINA (ver a prop `big`): respiro de 28px por 48px e a barra de
     * cor de 12px para 16px - o primeiro degrau da escala em que ela fica visivelmente mais
     * grossa, já que o de cima (14px) muda 2px e não se vê.
     *
     * A REDUÇÃO NO ESTREITO É A PARTE QUE IMPORTA: com 48px de cada lado, uma caixa de 320px
     * fica com 224px para a frase, e a pwa já reduzia isso (`padding: s` no mobile dela). O
     * degrau é o do CONTAINER e é o mesmo 480px que este arquivo já usa acima - quem aperta
     * a frase é a caixa, não o aparelho, e uma coluna estreita numa página de desktop aperta
     * igual. Repare que 18px continua acima dos 14px do padrão: no estreito ele deixa de ser
     * grande, não vira pequeno.
     */
    &.variant--big {
      --border-width: 0 0 0 var(--cordel-spacing-xs);
      // Linha e coluna separadas: a coluna é o que separa o bloco de texto da ação, e a
      // LINHA fica onde está - ela só aparece quando a linha quebra. O respiro entre o
      // símbolo e a frase, que também cresce com o aviso (era `margin-right: xl` no símbolo
      // da pwa), é do `.credere-alert-content`.
      --gap: var(--cordel-spacing-xxxs) var(--cordel-spacing-xl);
      --padding: var(--cordel-spacing-xxxl) var(--cordel-spacing-blaster);

      // O BLOCO DE TEXTO E A AÇÃO CENTRAM UM CONTRA O OUTRO, como na pwa: com o respiro
      // grande, o alinhamento ao topo deixava a ação e o `×` pendurados no alto de uma
      // caixa alta. O SÍMBOLO NÃO ENTRA NESSA CONTA - ele fica na primeira linha do texto,
      // e quem cuida disso é o `.credere-alert-content`. No aviso padrão o `×` continua no
      // topo, que é onde ele precisa ficar quando a frase ocupa mais de uma linha.
      align-items: center;

      @container (max-width: 480px) {
        --padding: var(--cordel-spacing-s);
      }
    }

    &.variant--light {
      --background-color: var(--cordel-color-highlight-light);
      --color: var(--cordel-color-constant-dark);
    }

    &.color--success {
      --border-color: var(--cordel-color-feedback-success);

      &.variant--light {
        --border-color: var(--cordel-color-highlight-dark);
      }
    }

    &.color--information {
      --border-color: var(--cordel-color-feedback-information);
    }

    b {
      --font-weight: var(--cordel-font-weight-bold);
    }
  }

  /*
   * O SÍMBOLO NA PRIMEIRA LINHA DO TEXTO, que é o que este elemento existe para dizer:
   * `flex-start` aqui vale contra a altura do TEXTO e não contra a da linha inteira, onde a
   * ação de 43.6px é quem manda.
   *
   * Ele toma a folga da linha (`flex: 1`) porque era o que a frase fazia antes de ganhar
   * este agrupador - a ação e o `×` continuam empurrados para a ponta -, e o `min-width: 0`
   * sobe junto: um item de flex se recusa a encolher abaixo da palavra mais longa que
   * carrega, e essa recusa é o que empurrava o `×` para fora do aviso (60px além da caixa,
   * medido). A frase mantém o dela, porque a mesma recusa acontece um nível abaixo.
   */
  .credere-alert-content {
    --gap: var(--cordel-spacing-xxxs);

    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: flex-start;
    min-width: 0;

    // O respiro do aviso grande, que na pwa era um `margin-right: xl` no próprio símbolo.
    .variant--big & {
      --gap: var(--cordel-spacing-xl);
    }
  }

  .credere-alert-icon {
    --background-color: var(--cordel-color-feedback-danger);
    --border-radius: var(--cordel-border-radius-circular);
    --color: var(--cordel-color-constant-dark);
    --font-size: var(--cordel-font-size-s);
    --font-weight: var(--cordel-font-weight-bold);
    --margin: 0.25rem 0 0;

    flex-shrink: 0;
    height: 1rem;
    line-height: 1rem;
    text-align: center;
    width: 1rem;

    /*
     * NO `big` O SÍMBOLO CRESCE, como fazia na pwa, onde o glifo ia a `1.6em`.
     *
     * O EMPURRÃO DE 0.25rem SAI, e não é por causa do alinhamento: ele existia para o disco
     * de 1rem encostar na primeira linha do texto, que é mais alta que ele (21px contra
     * 16px). Aqui o disco tem 1.6rem e é o mais alto dos dois - encostado no topo, já cobre
     * a linha inteira -, então não há empurrão a dar.
     *
     * O disco continua medido em `rem` cru, como já era - fator 1.6, o mesmo da pwa. O
     * glifo dentro dele é que sobe na escala de tipo (12px para 18px), porque texto tem
     * escala e caixa de disco não tem.
     */
    .variant--big & {
      --font-size: var(--cordel-font-size-xl);
      --margin: 0;

      height: 1.6rem;
      line-height: 1.6rem;
      width: 1.6rem;
    }

    .color--success & {
      --background-color: var(--cordel-color-feedback-success);
    }

    .color--information & {
      --background-color: var(--cordel-color-feedback-information);
    }
  }

  // Takes the slack of the row it shares with the symbol, so the two together push the
  // action and the `×` to the far end.
  .credere-alert-paragraph {
    display: block;
    flex: 1;

    /*
     * The global `overflow-wrap` in `app.scss` breaks the long token; this is
     * the other half, and it has to be local. A flex item defaults to
     * `min-width: auto`, so it refuses to shrink below its longest word - and
     * with the close control as a sibling that refusal shoves the control clean
     * out of the alert rather than wrapping (measured: 60px past the container).
     * Saying so here, and not globally, is deliberate: letting *every* text
     * block shrink to nothing is what collapsed a banner headline into a column
     * one letter wide. The text is the part of an alert that should give.
     *
     * It is declared HERE AND ON THE GROUPER, because the refusal happens once
     * per flex item: the grouper is the item the row measures, the sentence is
     * the item the grouper measures, and either one of them holding its longest
     * word is enough to push the `×` out.
     */
    min-width: 0;
  }

  // Beside the sentence, at the end of the row. The space between the two is the
  // row's own gap and nothing else - a margin here would stack on top of it.
  // `flex-shrink: 0` keeps the button at its own width while the text takes the
  // rest, and `align-self` centres it against a sentence that may wrap to
  // several lines: the row itself is `flex-start`, which would pin it to the
  // first line.
  .credere-alert-action {
    align-items: center;
    align-self: center;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: flex-end;

    @container (max-width: 480px) {
      /*
       * `order` is what puts the close back on the first line. In the markup the
       * action comes BEFORE the close, so a plain wrap would break after the
       * action and leave the × alone on a third line - an item taking the whole
       * line means nothing else can share it.
       *
       * THE COST, because `order` always has one: the DOM order is the tab order
       * and it does not move, so stacked, focus goes action then × while the eye
       * reads × then action. The two agree in the wide row and disagree here.
       * No single DOM order fixes both - the action is visually last stacked and
       * second-to-last wide - so this is a choice about WHERE to put the
       * mismatch, and it belongs on the layout that a keyboard almost never
       * sees: stacked is the phone, where the order that matters is the one the
       * thumb follows. Nothing automated will catch it either; axe has no rule
       * here, because it cannot compute visual order. Recorded in
       * DESIGN-PRINCIPLES §7.
       */
      flex-basis: 100%;
      order: 1;
    }
  }

  // Full width on its own line, the way `Core/CalloutCard` fills its own button -
  // the wrapper claiming the line is not enough on its own, since the button is a
  // flex item that would otherwise sit at its label's width.
  .credere-alert-action-button {
    @container (max-width: 480px) {
      width: 100%;
    }
  }

  .credere-alert-close {
    --border-radius: var(--cordel-border-radius-small);
    --color: var(--cordel-color-constant-light);
    --font-size: var(--cordel-font-size-l);
    --line-height: var(--cordel-line-height-s);
    --padding: var(--cordel-spacing-xxs);

    /*
     * The padding is what makes the target, and the equal negative margin is
     * what keeps it from costing anything: the button's box grows to 47px
     * square - clear of the 44px floor in Phase 2 and WCAG 2.5.5 - and then
     * hangs back over the alert's own `--cordel-spacing-xxs` padding, so the
     * glyph lands on the padding edge instead of pushing the row taller. Left
     * is `auto` because the control belongs at the end of the row, and the
     * house convention spells that as a margin rather than `margin-left`.
     */
    --margin: calc(var(--cordel-spacing-xxs) * -1)
              calc(var(--cordel-spacing-xxs) * -1)
              calc(var(--cordel-spacing-xxs) * -1)
              auto;

    align-items: center;
    appearance: none;

    // The square-icon-button formula from `mixins/button.scss`'s
    // `variant--iconed`: the glyph is narrower than it is tall, so a padded box
    // alone would be 42px wide and miss the floor on one axis only.
    aspect-ratio: 1 / 1;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: center;
    min-width: calc(var(--cordel-spacing-xxs) * 2 + var(--font-size) * var(--line-height));

    /*
     * The UA ring has to go, and not for taste: it paints over the token ring
     * below - measured, both drew at once - and it arrives in the browser's own
     * blue, on a widget whose whole point is to look like the dealer's page.
     * `mixins/button.scss` settles focus the same way, so the ring is one idiom
     * across the product rather than two.
     */
    outline: none;
    transition: box-shadow .1s, color .1s, transform .1s;

    /*
     * The glyph is the same white as the body text, so it reads at 15.9:1 and,
     * more to the point, reads the same on all three colours - `color` moves
     * the left border and the icon chip, never the surface, so there is no
     * per-colour contrast case to get wrong. Hover, focus and active borrow the
     * Button's state vocabulary rather than inventing a third one, which is why
     * hover is `highlight-primary` (8.3:1 on the ink surface) and not a tint of
     * the alert's own colour - and why the ring below takes the system's focus
     * colour instead of the hover one.
     */
    &:hover {
      --color: var(--cordel-color-highlight-primary);
    }

    /*
     * Ring on `:focus`, then taken back for the pointer case. Written in this
     * order rather than as a bare `:focus-visible` so that a browser without
     * `:focus-visible` drops the second rule as invalid and keeps the first -
     * it lands on the rest of the system's mouse-clicks-show-a-ring behaviour
     * instead of on no focus indicator at all, which is the one outcome
     * `outline: none` above must never be able to produce.
     */
    &:focus {
      --box-shadow: 0 0 0 var(--cordel-border-width-heavy) var(--cordel-color-feedback-information);
    }

    &:focus:not(:focus-visible) {
      --box-shadow: none;
    }

    &:active {
      transform: translateY(2px);
    }
  }
</style>
